import React, {useEffect, useRef, useState} from 'react';
import {
    ButtonPedidos,
    ContainerLeft,
    ContainerPrecios,
    ContainerRight,
    EtiquetaPrecio,
    EtiquetasPedido,
    Longcard,
    OrdenPedido,
    Precios,
    PreciosDiv,
    PreciosPedidoEtiqueta,
    ViewCart,
    formatEuro
} from '../../user/ui/cuenta.elements';
import 'react-toastify/dist/ReactToastify.css';
import {ContainerPDF, Indicador, ParrafoIndicador} from '../../ui';
import {DownloadableOrderRecap} from './DownloadableOrderRecap';
import styled from 'styled-components';
import {useTenantContext} from '../../tenant/TenantContext';
import {useComercialContext} from '../../comercial/ComercialContext';
import {useUserContext} from '../../user/infrastructure/UserContext';
import {downloadCmpAsPdf} from '../../helpers/downloadBlob';
import {ProductsModal} from '../../product';
import {getAll} from '../infrastructure/OrderApi';

// Interruptor del estado del envio en el badge (false = "Enviado" a secas + enlace).
// Encendido el 1-sep con el OK del usuario tras cuadrar los datos de Sendcloud.
const MOSTRAR_ESTADO_ENVIO = true;

// El seguimiento de envios se esta rodando SOLO con comerciales (decision usuario
// 1-sep); cuando lo validen, quitar esta condicion para abrirlo a los clientes.
const ENVIOS_SOLO_COMERCIALES = true;

// Colores por estado al estilo Amazon: texto con peso y color semantico, sin fondo
// de pildora ni emoji. Lo no reconocido (estados nuevos de Sendcloud) va en neutro.
const COLORES_ESTADO = {
    'Entregado': '#007600',
    'En reparto': '#c45500',
    'En tránsito': '#c45500',
    'Preparando envío': '#565959',
    'Incidencia': '#d00000',
};

const EstadoEnvio = styled.span`
    font-weight: 700;
    font-size: 0.95rem;
    color: ${({ $estado }) => COLORES_ESTADO[$estado] || '#565959'};
`;

const FechaEnvio = styled.span`
    color: #565959;
    font-size: 0.85rem;
`;

// Barra de progreso del envio al estilo Amazon: linea con puntos por etapa, rellena
// hasta la situacion actual (verde; rojo si incidencia).
const PASOS_ENVIO = ['Preparado', 'En tránsito', 'En reparto', 'Entregado'];
const POSICIONES_PASO = [0, 33.33, 66.66, 100];

const pasoDeEstado = (estado) => {
    switch (estado) {
        case 'Preparando envío': return 0;
        case 'En reparto': return 2;
        case 'Entregado': return 3;
        // En tránsito, Enviado sin detalle, incidencias y estados raros: en camino.
        default: return 1;
    }
};

const BarraEnvio = styled.div`
    position: relative;
    max-width: 400px;
    height: 36px;
    margin: 0.45rem 7px 0.2rem;
`;

const LineaEnvio = styled.div`
    position: absolute;
    top: 5px;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 2px;
    background: #e6e6e6;
`;

const LineaProgresoEnvio = styled.div`
    position: absolute;
    top: 5px;
    left: 0;
    height: 4px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
    width: ${({ $pct }) => $pct}%;
`;

const PuntoEnvio = styled.div`
    position: absolute;
    top: 0;
    left: ${({ $x }) => $x}%;
    transform: translateX(-50%);
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ $activo, $color }) => ($activo ? $color : '#ffffff')};
    border: 2px solid ${({ $activo, $color }) => ($activo ? $color : '#c9c9c9')};
`;

const EtiquetaPasoEnvio = styled.div`
    position: absolute;
    top: 17px;
    left: ${({ $x }) => $x}%;
    transform: translateX(${({ $x }) => ($x === 0 ? '0' : $x === 100 ? '-100%' : '-50%')});
    font-size: 0.68rem;
    white-space: nowrap;
    color: ${({ $activo }) => ($activo ? '#0f1111' : '#8a8a8a')};
    font-weight: ${({ $actual }) => ($actual ? 700 : 400)};
`;

const ProgresoEnvio = ({ estado }) => {
    const paso = pasoDeEstado(estado);
    const color = estado === 'Incidencia' ? '#d00000' : '#007600';
    return (
        <BarraEnvio>
            <LineaEnvio />
            <LineaProgresoEnvio $pct={POSICIONES_PASO[paso]} $color={color} />
            {PASOS_ENVIO.map((label, i) => (
                <React.Fragment key={label}>
                    <PuntoEnvio $x={POSICIONES_PASO[i]} $activo={i <= paso} $color={color} />
                    <EtiquetaPasoEnvio $x={POSICIONES_PASO[i]} $activo={i <= paso} $actual={i === paso}>
                        {label}
                    </EtiquetaPasoEnvio>
                </React.Fragment>
            ))}
        </BarraEnvio>
    );
};

function OrderHistory() {
    const tenant = useTenantContext();
    const { primaryColor, secondaryColor } = tenant;
    // En modo comercial suplantando, el historial es el del cliente que se mira.
    const { actingAsCliente } = useComercialContext();
    const user = useUserContext();
    // Rodaje: los envios solo los ven los comerciales hasta que se valide.
    const mostrarEnvios = !ENVIOS_SOLO_COMERCIALES || !!user?.isComercial;
    const [orders, setOrders] = useState([]);
    const [orderData, setOrderData] = useState();
    const [showModals, setShowModals] = useState({});

    const GetOrders = async () => {
        try {
            setOrders(await getAll(actingAsCliente?.numCliente ?? null));
        } catch (error) {
            alert('Error al cargar historial de pedidos');
        }
    };

    const certificateTemplateRef = useRef(null);

    const downloadOrdersPdf = async (order) => {
        setOrderData(order);
        await downloadCmpAsPdf(certificateTemplateRef, `order-${order.numOrden}`);
    };

    useEffect(() => {
        GetOrders().catch(error => console.log(error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actingAsCliente]);

    return (
        <div className="d-flex flex-column gap-5">
            <div>
                <Indicador indicador={'Historial de pedidos'} />
                <ParrafoIndicador
                    parrafo={actingAsCliente
                        ? `Pedidos del cliente ${actingAsCliente.numCliente}${actingAsCliente.nombreRazonSocial ? ` - ${actingAsCliente.nombreRazonSocial}` : ''} (web y de comerciales).`
                        : 'Revisa el historial de tus pedidos, su estado y descarga tus pedidos.'} />
                <div className="row">
                    {orders.map((pedido, index) => (
                        <Longcard className="col-md-12" key={index}>
                            <div className="row">
                                <ContainerLeft className="col-md-6">
                                    <EtiquetasPedido>Fecha de pedido</EtiquetasPedido>
                                    <EtiquetaPrecio background={secondaryColor}>{pedido.fechaPedido}</EtiquetaPrecio>
                                    <OrdenPedido>Numero de pedido: {pedido.numOrden}</OrdenPedido>
                                    {/* Envios del pedido (albaranes de BC): salen cuando el almacen
                                        expide; con agencia via Sendcloud llevan enlace de seguimiento. */}
                                    {mostrarEnvios && (pedido.envios || []).map(envio => (
                                        <div key={envio.albaran} className="mt-1">
                                            <div className="d-flex align-items-baseline flex-wrap gap-2">
                                                <EstadoEnvio $estado={MOSTRAR_ESTADO_ENVIO ? (envio.estado || 'Enviado') : 'Enviado'}>
                                                    {MOSTRAR_ESTADO_ENVIO ? (envio.estado || 'Enviado') : 'Enviado'}
                                                </EstadoEnvio>
                                                {envio.fecha && <FechaEnvio>enviado el {envio.fecha}</FechaEnvio>}
                                                {envio.trackingUrl ? (
                                                    <a href={envio.trackingUrl} target="_blank" rel="noreferrer">
                                                        Seguir envío
                                                    </a>
                                                ) : envio.trackingNo ? (
                                                    <FechaEnvio>Expedición {envio.trackingNo}</FechaEnvio>
                                                ) : (
                                                    <FechaEnvio>Sin información de seguimiento</FechaEnvio>
                                                )}
                                            </div>
                                            {/* La barra solo con seguimiento real: en reparto propio no hay
                                                estados y pintarla seria inventarselos. */}
                                            {MOSTRAR_ESTADO_ENVIO && envio.trackingNo && (
                                                <ProgresoEnvio estado={envio.estado || 'Enviado'} />
                                            )}
                                        </div>
                                    ))}
                                </ContainerLeft>
                                <ContainerRight className="col-md-6">
                                    <ViewCart
                                        color={primaryColor}
                                        onClick={() => setShowModals({
                                            ...showModals,
                                            [pedido.id]: !showModals[pedido.id]
                                        })}>
                                        {pedido.unidades} productos en este pedido
                                    </ViewCart>
                                    {showModals[pedido.id] && (
                                        <ProductsModal
                                            title={`Productos - Pedido ${pedido.numOrden}`}
                                            onModalClose={() => setShowModals({
                                                ...showModals,
                                                [pedido.id]: !showModals[pedido.id]
                                            })}
                                            products={pedido.articulos} />
                                    )}
                                    <div className="row">
                                        <div className="col-md-12">
                                            <ButtonPedidos
                                                text
                                                background={primaryColor}
                                                onClick={() => downloadOrdersPdf(pedido)}>
                                                Descargar pedido
                                            </ButtonPedidos>
                                        </div>
                                    </div>
                                    <ContainerPrecios>
                                        <PreciosDiv />
                                        <PreciosDiv>
                                            <PreciosPedidoEtiqueta>Descuentos</PreciosPedidoEtiqueta>
                                            <Precios background={primaryColor}>{formatEuro(pedido.descuentos)}</Precios>
                                        </PreciosDiv>
                                        <PreciosDiv>
                                            <PreciosPedidoEtiqueta>Subtotal</PreciosPedidoEtiqueta>
                                            <Precios>{formatEuro(pedido.subtotal)}</Precios>
                                        </PreciosDiv>
                                        <PreciosDiv>
                                            <PreciosPedidoEtiqueta>IVA</PreciosPedidoEtiqueta>
                                            <Precios>{formatEuro(pedido.iva)}</Precios>
                                        </PreciosDiv>
                                        <PreciosDiv>
                                            <PreciosPedidoEtiqueta>Total</PreciosPedidoEtiqueta>
                                            <Precios>{formatEuro(pedido.precioTotal)}</Precios>
                                        </PreciosDiv>
                                    </ContainerPrecios>
                                </ContainerRight>
                            </div>
                        </Longcard>
                    ))}
                </div>
                <ContainerPDF ref={certificateTemplateRef}>
                    <DownloadableOrderRecap order={orderData} />
                </ContainerPDF>
            </div>
        </div>
    );
}

export {OrderHistory};
